import { useEffect, useState } from 'react';
import {
  decodeText,
  isFeather2FormatFromBlob,
} from '@/modules/file-renderer/utils';
import { useFileMetadata } from '@/shared/hooks';
import { parseSizeBytes } from '@/shared/utils';
import { parseFileResourceName } from '@/shared/utils/file-utils';
import { isFile, isTable } from '../utils';
import type { TResolvedValue } from '../types';
import { OPENABLE_MAX_FILE_BYTES } from '../constants';
import {
  getOutputSourceViewText,
  isCsvFile,
} from '../components/output-card-utils';
import { base64ToArrayBuffer } from '../utils/base64';
import { useFileDownload } from './useFileDownload';

/**
 * Resolves the raw text `getOutputSourceViewText` returns synchronously for
 * HTML and an inline CSV, plus the cases whose text only exists after a
 * download or a base64 decode: a remote CSV table and a CSV `FILE` (inline or
 * remote). `null` means either there's nothing to toggle to, or there is but
 * it hasn't resolved yet — the caller (the modal header) treats both the
 * same way, by not offering the toggle.
 *
 * Shares `useFileDownload`'s module-level cache with the renderer that draws
 * the value, so the bytes are never fetched twice, and resolves in the
 * background rather than blocking that renderer.
 */
export function useOutputSourceViewText(value: TResolvedValue): string | null {
  const syncText = getOutputSourceViewText(value);

  const remoteTableUrl =
    syncText === null && isTable(value) && value.type === 'table-remote'
      ? value.tableUrl
      : null;

  const isRemoteCsvFile =
    syncText === null &&
    isFile(value) &&
    value.type === 'file-remote' &&
    isCsvFile(value);
  const remoteCsvUrl = isRemoteCsvFile ? value.fileUrl : null;
  // Same size ceiling as `FileRenderer`'s expanded mode: a file too large to open never gets a toggle either.
  const parsedRemoteCsvFile = remoteCsvUrl
    ? parseFileResourceName(remoteCsvUrl)
    : null;
  const { data: remoteCsvMetadata, isPending: isRemoteCsvMetadataPending } =
    useFileMetadata(parsedRemoteCsvFile);
  const remoteCsvSizeBytes = parseSizeBytes(
    remoteCsvMetadata?.data?.size_bytes,
  );
  const isRemoteCsvOversized =
    !!parsedRemoteCsvFile &&
    remoteCsvSizeBytes !== null &&
    remoteCsvSizeBytes > OPENABLE_MAX_FILE_BYTES;
  const shouldSkipRemoteCsvDownload =
    !!parsedRemoteCsvFile &&
    (isRemoteCsvMetadataPending || isRemoteCsvOversized);

  const inlineCsvContent =
    syncText === null &&
    isFile(value) &&
    value.type === 'file-inline' &&
    isCsvFile(value)
      ? value.content
      : null;

  const { fileData: remoteTableFile } = useFileDownload(remoteTableUrl);
  const { fileData: remoteCsvFile } = useFileDownload(
    shouldSkipRemoteCsvDownload ? null : remoteCsvUrl,
  );

  const [resolvedText, setResolvedText] = useState<string | null>(null);

  // eslint-disable-next-line no-restricted-syntax -- reason: decoding a downloaded blob or a base64 payload is inherently async and must not block the renderer that draws the same value.
  useEffect(() => {
    if (syncText !== null) return;

    let cancelled = false;

    const resolve = async () => {
      if (remoteTableFile) {
        const isFeather2 = await isFeather2FormatFromBlob(remoteTableFile.blob);
        const text = isFeather2 ? null : await decodeText(remoteTableFile.blob);
        if (!cancelled) setResolvedText(text);
        return;
      }
      if (remoteCsvFile) {
        const text = await decodeText(remoteCsvFile.blob);
        if (!cancelled) setResolvedText(text);
        return;
      }
      if (inlineCsvContent !== null) {
        try {
          const text = new TextDecoder().decode(
            base64ToArrayBuffer(inlineCsvContent),
          );
          if (!cancelled) setResolvedText(text);
        } catch {
          if (!cancelled) setResolvedText(null);
        }
        return;
      }
      if (!cancelled) setResolvedText(null);
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [syncText, remoteTableFile, remoteCsvFile, inlineCsvContent]);

  return syncText ?? resolvedText;
}
